import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { RotateCcw, Loader2, AlertTriangle } from "lucide-react";

export const MOTIVOS_RESET = ["Material/marca equivocada", "Fotos/reportes de otro montaje", "Montaje incorrecto", "Error de aprobación", "Incidencia administrativa", "Otro"];

/** Botón + diálogo "Reabrir / Resetear montaje" (mismo endpoint desde RutaDetail y Reportes). */
export default function ResetParadaDialog({ rutaId, orden, titulo, onDone, size = "sm", className = "" }) {
  const [open, setOpen] = useState(false);
  const [motivo, setMotivo] = useState(MOTIVOS_RESET[1]);
  const [detalle, setDetalle] = useState("");
  const [busy, setBusy] = useState(false);

  const ejecutar = async () => {
    if (motivo === "Otro" && !detalle.trim()) { toast.error("Indica el detalle del motivo"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/rutas/${rutaId}/paradas/${orden}/resetear`, { motivo, detalle: detalle.trim() || null });
      if (data.ya_reseteada) toast.info("Esta parada ya estaba reseteada; no se ha cambiado nada");
      else toast.success(`Parada reseteada · ${data.evidencias_archivadas} evidencia(s) retiradas del cliente y archivadas. La línea vuelve a estar disponible para rutar.`, { duration: 9000 });
      setOpen(false);
      onDone?.(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? d?.msg || "Error" : d || "Error al resetear", { duration: 8000 });
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size={size} variant="outline" onClick={() => setOpen(true)} data-testid={`resetear-parada-${orden}`}
        className={`border-rose-500/50 text-rose-200 hover:bg-rose-500/15 bg-transparent h-7 text-xs ${className}`}
        title="Corrección post-cierre: retira el reporte del cliente, reabre la línea y permite volver a enrutarla">
        <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reabrir / Resetear montaje
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="reset-parada-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-rose-400" /> Reabrir / Resetear montaje</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-zinc-300">Parada <b>#{orden}</b>{titulo ? <> · <b>{titulo}</b></> : null}</div>
            <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-3 text-xs text-rose-100">
              Esta acción <b>retirará el reporte del cliente</b> (fotos, vídeos y aprobación dejan de verse en el portal), <b>reabrirá la línea</b> y sus componentes,
              y permitirá <b>volver a enrutarla</b>. La ruta se conserva como histórico y <b>todo queda registrado</b> (quién, cuándo, motivo, evidencias archivadas).
            </div>
            <div>
              <div className="text-[10px] uppercase text-zinc-500 mb-1">Motivo (obligatorio)</div>
              <Select value={motivo} onValueChange={setMotivo}>
                <SelectTrigger data-testid="reset-motivo" className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">{MOTIVOS_RESET.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Textarea value={detalle} onChange={(e) => setDetalle(e.target.value)} data-testid="reset-detalle" rows={2}
              placeholder={motivo === "Otro" ? "Detalle obligatorio…" : "Detalle opcional (p.ej. fotos eran de Dolce & Gabbana)"} className="bg-zinc-900 border-zinc-800 text-sm" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancelar</Button>
            <Button onClick={ejecutar} disabled={busy} data-testid="reset-confirmar" className="bg-rose-600 hover:bg-rose-500 text-white font-semibold">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />} Sí, resetear montaje
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
